import React from "react";
import {
  isRightToLeft,
  nativeLanguageName
} from "../../shared/i18n/native-language-names";
import type { LanguageOption } from "../types";

interface LanguagePillProps {
  readonly language: LanguageOption;
  readonly transcriptUrl: string;
}

/**
 * A link, not a label: choosing a language is the point, and it opens that language's transcript.
 *
 * `lang` and `dir` are load-bearing -- without them a screen reader reads 한국어 in an English
 * voice. `hrefLang` says where the link leads before it is followed, and the new-tab warning is
 * required because a link that repurposes the window without saying so fails WCAG 3.2.5.
 *
 * role="listitem" because an anchor is not a list item natively, and the parent carries role="list".
 */
export function LanguagePill({
  language,
  transcriptUrl
}: LanguagePillProps): React.JSX.Element {
  const native = nativeLanguageName(language.shortCode, language.englishName);
  const separator = transcriptUrl.includes("?") ? "&" : "?";

  return (
    <a
      className="langpill la-pill"
      role="listitem"
      href={`${transcriptUrl}${separator}lang=${encodeURIComponent(language.shortCode)}`}
      hrefLang={language.shortCode}
      target="_blank"
      rel="noopener"
    >
      <span
        className="auto la-pill__native"
        lang={language.shortCode}
        dir={isRightToLeft(language.shortCode) ? "rtl" : undefined}
      >
        {native}
      </span>
      {native !== language.englishName && (
        <span className="en la-pill__english">{language.englishName}</span>
      )}
      <span className="visually-hidden"> transcript (opens in a new tab)</span>
    </a>
  );
}
